import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { shouldShowSendOnBehalfUI } from '../../../../models/permissions/sendOnBehalf';
import { ISelectOption } from '../../../components/DeprecatedSelect';
import { FabContext } from '../../../components/FabContext';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { UserSelectBox } from '../../../components/UserSelectBox';
import { AccountSendFromDropdown } from '../../MultiCampaignScheduler/components/MultiCampaignSchedulerLayout';
import { CampaignSessionManager } from '../../shared/AIScheduleSuccess/campaignSessionManager';
import { REDUCER_ACTION_KEYS, useBlogPostEditorContext } from './hooks';
import { styleSheet } from './styles';

export interface IBlogPostEditorLayoutProps extends IImpersonationContextComponentProps {
	header?: React.ReactNode;
	sidebar?: React.ReactNode;
	status?: Api.BlogStatus;
	subHeader?: React.ReactNode;
	onClose?: () => void;
}

const BlogPostCreatorDropdown = inject(ImpersonationContextKey)(
	observer(function BlogPostCreatorDropdown({ impersonationContext }: IImpersonationContextComponentProps) {
		const userSession = useUserSession();
		const { state, dispatch } = useBlogPostEditorContext();

		const initialSelectedUser =
			state.sendFromUser ||
			(impersonationContext?.isValid && impersonationContext?.user ? impersonationContext.user : null);
		const [initialSelectedUserOption] = React.useState<ISelectOption<Api.UserViewModel<Api.IUser>>>(() => {
			if (initialSelectedUser) {
				const activeUser = new Api.UserViewModel(userSession, initialSelectedUser).impersonate(impersonationContext);
				return {
					dataContext: activeUser,
					id: activeUser.id,
					text: activeUser.name,
				};
			}
		});

		const onAccountSendFromChange = (_: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
			if (impersonationContext) {
				impersonationContext.update({
					account: impersonationContext.account,
					user,
				});
			}
			dispatch({ type: REDUCER_ACTION_KEYS.SET_SEND_ON_BEHALF_OF_USER, payload: user });
		};

		const onUserOptionSelected = async (option: ISelectOption<Api.UserViewModel>) => {
			const user = option.dataContext;
			if (impersonationContext) {
				impersonationContext.update({
					account: impersonationContext.account,
					user,
				});
				user.impersonate(impersonationContext);
			}
			dispatch({ type: REDUCER_ACTION_KEYS.SET_SEND_ON_BEHALF_OF_USER, payload: user.toJs() });
		};

		const isUsingAccountImpersonation = impersonationContext?.isValid ?? false;
		const canSendOnBehalf = shouldShowSendOnBehalfUI({
			isUsingAccountImpersonation,
			userRole: userSession.userRole,
			sendOnBehalfEnabled: userSession.account?.preferences?.sendOnBehalfEnabled,
		});

		const label = (
			<label>
				Post creator <sup>*</sup>
			</label>
		);

		return (
			<div className={css(styleSheet.sendFromDropdownContainer)}>
				{canSendOnBehalf ? (
					<>
						{isUsingAccountImpersonation ? (
							<AccountSendFromDropdown
								sendFromUserId={initialSelectedUser?.id}
								onSendFromChange={onAccountSendFromChange}
								impersonationContext={impersonationContext}
								isSocialMediaLayout={false}
								userSelectLabel={label}
								hideContactOwnerOption
							/>
						) : null}

						{!isUsingAccountImpersonation ? (
							<>
								{label}
								<UserSelectBox
									defaultOption={null}
									includeSearch={userSession.account.isAdmin}
									onOptionSelected={onUserOptionSelected}
									onRenderPlaceholder={() => <span>Select a User</span>}
									initialSelectedOption={initialSelectedUserOption}
								/>
							</>
						) : null}
					</>
				) : null}
			</div>
		);
	})
);

export const BlogPostEditorLayout = inject(ImpersonationContextKey)(
	observer(function BlogPostEditorLayout({
		header,
		subHeader,
		children,
		sidebar,
		status,
		onClose,
		impersonationContext,
	}: React.PropsWithChildren<IBlogPostEditorLayoutProps>) {
		const userSession = useUserSession();

		const isPending = status === Api.BlogStatus.Pending;
		const isUsingAccountImpersonation = impersonationContext?.isValid ?? false;
		const sendOnBehalfEnabled = shouldShowSendOnBehalfUI({
			isUsingAccountImpersonation,
			userRole: userSession.userRole,
			sendOnBehalfEnabled: userSession.account?.preferences?.sendOnBehalfEnabled,
		});

		const handleClose = React.useCallback(() => {
			CampaignSessionManager.clearSession();
			onClose?.();
		}, [onClose]);

		return (
			<div className={css(styleSheet.mainContainer)}>
				<MultiContainerHeader fullscreenHeader={header} onFullscreenRequestClose={handleClose} />
				<div className={css(styleSheet.container)}>
					<section className={css(styleSheet.subHeader)}>
						<div className={css(styleSheet.subHeaderContent)}>{subHeader}</div>
						{sendOnBehalfEnabled && !isPending ? <BlogPostCreatorDropdown /> : null}
					</section>
					<div className={css(styleSheet.contentWrap)}>{children}</div>
				</div>
				<section className={css(styleSheet.sidebar, styleSheet.sidebarCenter)}>{sidebar}</section>
				<FabContext appearance={{ hidden: true }} />
			</div>
		);
	})
);
